import buffsDb from '../data/Player/buffs_db.json'
import skillInfo from '../data/skillInfo.json'
import {
	SWORDMAN,
	MAGE,
	ARCHER,
	ACOLYTE,
	MERCHANT,
	THIEF,
	EAJL_2,
	EAJL_2_1,
	EAJL_2_2,
	EAJL_BABY,
	THIRDMASK,
	UPPERMASK,
	IS_MALE,
} from '../data/jobID'

// ─────────────────────────────────────────────────────────────────────────────
// Party / external buffs.
//
// Buffs are now managed entirely from the buff panel (BattleTab) as a map
// { [skillAegis]: level }; nothing is auto-applied from learned skills anymore.
// Each buff's effect is data-driven (src/data/Player/buffs_db.json):
//   value(lv) = base + per * lv
// ─────────────────────────────────────────────────────────────────────────────

// Nested structure: baseClass → { subGroup (1st / final class) → buff[] }.
// (skips the JSON _comment field)
export const BUFF_TREES = Object.fromEntries(
	Object.entries(buffsDb).filter(([key]) => key !== '_comment'),
)
// Flat list of every buff, used by the applier.
export const BUFF_LIST = Object.values(BUFF_TREES).flatMap((groups) =>
	Object.values(groups).flat(),
)

// Server skill names (AegisName → SkillName) from skillInfo.json. Used to label
// buffs with the live server names; falls back to the buff's own name when the
// skill isn't present in the data.
const SKILL_NAME = {}
for (const v of Object.values(skillInfo)) {
	if (v && v['1']) SKILL_NAME[v['1']] = v.SkillName
}

export function buffDisplayName(buff) {
	return SKILL_NAME[buff.skill] || buff.name
}

// ─── Self-buff visibility ─────────────────────────────────────────────────────
// A buff's own AegisName isn't always present in the app's (incomplete) skill
// data, so visibility is decided from the job id itself: a 'self' buff is shown
// only to jobs of the same base class, and — for a final-class sub-group — only
// to the matching 2nd-job branch (EAJL_2_1 vs EAJL_2_2). This is independent of
// the skill database.

const BASE_TREE = {
	[SWORDMAN]: 'Swordman',
	[ACOLYTE]: 'Acolyte',
	[ARCHER]: 'Archer',
	[MERCHANT]: 'Merchant',
	[THIEF]: 'Thief',
	[MAGE]: 'Magician',
}

// Final-class sub-group → required 2nd-job branch bit.
const SUBGROUP_BRANCH = {
	'Rune Knight': EAJL_2_1,
	'Royal Guard': EAJL_2_2,
	'Arch Bishop': EAJL_2_1,
	Sura: EAJL_2_2,
	Ranger: EAJL_2_1,
	'Maestro/Wanderer': EAJL_2_2,
	Mechanic: EAJL_2_1,
	Genetic: EAJL_2_2,
	'Guillotine Cross': EAJL_2_1,
	'Shadow Chaser': EAJL_2_2,
	Warlock: EAJL_2_1,
	Sorcerer: EAJL_2_2,
}

function jobBaseTree(job) {
	const base = job & ~THIRDMASK & ~UPPERMASK & ~EAJL_2 & ~IS_MALE & ~EAJL_BABY
	return BASE_TREE[base] || null
}

// Location (base class / sub-group / self flag) of each buff, by skill.
const BUFF_LOCATION = {}
for (const [baseClass, groups] of Object.entries(BUFF_TREES)) {
	for (const [subGroup, list] of Object.entries(groups)) {
		for (const buff of list) {
			BUFF_LOCATION[buff.skill] = { baseClass, subGroup, self: !!buff.self }
		}
	}
}

/**
 * Drop active self-buffs that the given job can't use (party buffs are kept).
 * Returns a new map { [skill]: level }.
 */
export function cleanBuffs(buffs, job) {
	const out = {}
	for (const [skill, level] of Object.entries(buffs || {})) {
		if (!(Number(level) > 0)) continue
		const loc = BUFF_LOCATION[skill]
		// Unknown buffs are kept; self-buffs require the matching class/branch.
		if (loc && loc.self && !selfBuffVisible(loc.baseClass, loc.subGroup, job))
			continue
		out[skill] = Number(level)
	}
	return out
}

/**
 * Whether a self-buff in [baseClass][subGroup] should be shown for `job`.
 * Non-self buffs are always visible (handled by the caller).
 */
export function selfBuffVisible(baseClass, subGroup, job) {
	if (jobBaseTree(job) !== baseClass) return false
	// 1st-class sub-group (key === base class) → any branch of that class.
	if (subGroup === baseClass) return true
	const branchBit = SUBGROUP_BRANCH[subGroup]
	if (!branchBit) return true
	return (job & branchBit) !== 0
}

// Match the equipped weapon SubType against a buff's weapon requirement.
//   '2H'   → SubType starts with '2h' (2hSword, 2hAxe, 2hMace, 2hSpear, 2hStaff)
//   'Axe'  → SubType contains 'axe'   (1hAxe, 2hAxe)
//   'Mace' → SubType contains 'mace'  (Mace, 2hMace)
export function weaponMatches(subType, categories) {
	if (!categories || categories.length === 0) return true
	const st = (subType || '').toLowerCase()
	return categories.some((cat) => {
		if (cat === '2H') return st.startsWith('2h')
		if (cat === 'Axe') return st.includes('axe')
		if (cat === 'Mace') return st.includes('mace')
		return false
	})
}

// Blacksmith line: Blacksmith / Whitesmith / Mastersmith / Mechanic / Meister
// and baby variants — all share MERCHANT | EAJL_2_1 without the alchemist branch.
export function isBlacksmithLine(job) {
	return (
		(job & MERCHANT) === MERCHANT &&
		(job & EAJL_2_1) !== 0 &&
		(job & EAJL_2_2) === 0
	)
}

function addBonus(newBonuses, key, value, subKey) {
	if (!value) return
	if (key === 'bVariableCastrate') {
		// Special structure used by the script engine: array keyed 'All'.
		if (!newBonuses[key]) newBonuses[key] = []
		newBonuses[key]['All'] = (newBonuses[key]['All'] || 0) + value
		return
	}
	// Sub-keyed bonuses (e.g. bSkillAtk[skill], bMagicAtkEle[element]) are stored
	// as an object indexed by that sub-key.
	if (subKey != null && subKey !== '') {
		if (!newBonuses[key] || typeof newBonuses[key] !== 'object')
			newBonuses[key] = {}
		newBonuses[key][subKey] = (newBonuses[key][subKey] || 0) + value
		return
	}
	newBonuses[key] = (newBonuses[key] ? newBonuses[key] : 0) + value
}

/**
 * Apply every active buff to the bonus map.
 * @param {object} buffs          { [skillAegis]: level }
 * @param {object} newBonuses     bonus accumulator (mutated)
 * @param {string} weaponSubType  equipped Right_Hand SubType (for weapon-gated buffs)
 * @param {number} job            current job id (for job-dependent buffs)
 * @param {number} baseLevel      player base level (for level-scaled buffs)
 */
export function getBuffsBonuses(buffs, newBonuses, weaponSubType, job, baseLevel) {
	if (!buffs) return
	for (const buff of BUFF_LIST) {
		const lv = Number(buffs[buff.skill] || 0)
		if (lv <= 0) continue
		if (buff.weapon && !weaponMatches(weaponSubType, buff.weapon)) continue

		// A buff whose effect is entirely different per level uses 'byLevel'
		// (a { level: { bonuses, atkEle, … } } map). Otherwise the buff itself
		// holds the effects, scaled by level.
		const eff = buff.byLevel ? buff.byLevel[lv] : buff
		if (!eff) continue

		for (const b of eff.bonuses || []) {
			let value
			if (b.jobBonus) {
				// Job-dependent fixed value (e.g. Adrenaline Rush 30% / 25%).
				value = isBlacksmithLine(job)
					? b.jobBonus.blacksmith
					: b.jobBonus.other
			} else {
				// 'every' = step scaling: +per for every N levels (e.g. Wind Walker
				// +1 Flee per 2 levels → ceil(lv/2)). Otherwise linear per level.
				// In a 'byLevel' entry the level is fixed, so use 'base' as the value.
				const steps = b.every ? Math.ceil(lv / b.every) : lv
				value = (b.base || 0) + (b.per || 0) * steps
			}
			// 'timesBaseLevel' multiplies by the player base level
			// (e.g. Aura Blade bAtk = (3 + lv) * baseLevel).
			if (b.timesBaseLevel) value *= baseLevel || 1
			// 'keyed' = sub-key for indexed bonuses (e.g. bSkillAtk per skill).
			addBonus(newBonuses, b.key, value, b.keyed)
		}
		// Element-changing buffs (Aspersio, Enchant Poison) override weapon element.
		if (eff.atkEle) newBonuses['bAtkEle'] = eff.atkEle
		// DEF-ignoring buffs (Union) use the rAthena bIgnoreDefRace bonus,
		// stored like item scripts: an array of { Class: <race> } (RC_All = all).
		if (eff.ignoreDefRace) {
			if (!newBonuses['bIgnoreDefRace']) newBonuses['bIgnoreDefRace'] = []
			newBonuses['bIgnoreDefRace'].push({ Class: eff.ignoreDefRace })
		}
		// Recognized Spell removes MATK variance (magic damage always at max).
		if (eff.noMatkVariance) newBonuses['bNoMatkVariance'] = true
		// Buffs that set a wd.miscflag bit for the damage calc (e.g. Rising Dragon
		// → bit 1 = ">5 spirit balls", doubling Asura Strike).
		if (eff.miscflag)
			newBonuses['bMiscflag'] = (newBonuses['bMiscflag'] || 0) | eff.miscflag
	}
}

// True if any active buff ignores the size penalty (Weapon Perfection).
export function buffsIgnoreSizePenalty(buffs) {
	if (!buffs) return false
	return BUFF_LIST.some((b) => b.noSizeFix && Number(buffs[b.skill] || 0) > 0)
}
